import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import {
  Sparkles, ArrowLeft, Heart, History, Settings, BookOpen,
  Eye, Hand, Moon, Brain, Waves, ArrowRight, CheckCircle2
} from "lucide-react";
import { AppMenu } from "../components/AppMenu";
import { SiteFooter } from "../components/SiteFooter";

// Numbered step tile used in the "How It Works" row.
const StepTile = ({ num, icon: Icon, title, body, accent }) => (
  <div
    className="relative bg-white rounded-2xl p-5 border border-slate-100 shadow-[0_2px_14px_rgba(0,0,0,0.03)] hover:shadow-[0_6px_24px_rgba(0,0,0,0.06)] transition-shadow"
  >
    <div
      className="absolute -top-3 -left-3 w-8 h-8 rounded-full text-white text-sm font-semibold flex items-center justify-center shadow-[0_4px_12px_rgba(0,0,0,0.08)]"
      style={{ backgroundColor: accent }}
    >
      {num}
    </div>
    <div
      className="w-11 h-11 rounded-xl flex items-center justify-center mb-3"
      style={{ backgroundColor: `${accent}18` }}
    >
      <Icon className="w-5 h-5" style={{ color: accent }} />
    </div>
    <h4 className="font-outfit font-medium text-[#1B2349] mb-1">{title}</h4>
    <p className="text-sm text-[#718096] leading-relaxed">{body}</p>
  </div>
);

const GettingStartedPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FBF8F1]" data-testid="getting-started-page">
      {/* Header */}
      <header className="glass-header border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/logo-bilateral-calm.png"
              alt="Bilateral Calm"
              className="h-28 sm:h-32 w-auto"
            />
            <span className="font-outfit font-semibold text-xl text-[#1B2349]">Bilateral Calm</span>
          </div>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/dashboard" className="text-[#718096] hover:text-[#3090A8] transition-colors">Dashboard</Link>
            <Link to="/getting-started" className="text-[#1B2349] font-medium hover:text-[#3090A8] transition-colors">Getting Started</Link>
            <Link to="/history" className="text-[#718096] hover:text-[#3090A8] transition-colors">History</Link>
            <Link to="/favorites" className="text-[#718096] hover:text-[#3090A8] transition-colors">Favorites</Link>
          </nav>
          <div className="flex items-center gap-4">
            {user?.picture && <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />}
            <AppMenu />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 pb-24 app-prose">
        {/* Back button */}
        <button
          onClick={() => navigate('/dashboard')}
          className="inline-flex items-center gap-2 text-sm text-[#718096] hover:text-[#1B2349] mb-6 transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>

        {/* ============ HERO ============ */}
        <section className="relative overflow-hidden rounded-[28px] bg-gradient-to-br from-[#3090A8] via-[#9DB8A0] to-[#78C0D8] p-8 sm:p-12 text-white mb-10 animate-fade-in-up">
          {/* Decorative blurred orbs */}
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/10 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-20 -left-12 w-72 h-72 rounded-full bg-white/10 blur-3xl pointer-events-none" />

          <div className="relative">
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs font-medium mb-5">
              <Sparkles className="w-3.5 h-3.5" />
              Your guide to Bilateral Calm
            </div>
            <h1 className="font-outfit font-semibold text-3xl sm:text-5xl leading-tight mb-4">
              A calmer mind,<br className="hidden sm:block" />
              <span className="text-white/90">one gentle tone at a time.</span>
            </h1>
            <p className="text-white/90 text-base sm:text-lg max-w-2xl leading-relaxed">
              Learn the simple science behind bilateral stimulation and the two easy ways to follow along — so you can ease tension, soften the edges of a feeling, and help your mind do what it already knows how to do.
            </p>
          </div>
        </section>

        {/* ============ HOW IT WORKS ============ */}
        <section className="mb-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-6 bg-[#3090A8] rounded-full" />
            <h2 className="font-outfit font-medium text-xl text-[#1B2349]">How it works</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <StepTile
              num="1"
              icon={Brain}
              title="Hold a feeling"
              body="Bring to mind whatever's weighing on you — a worry, a memory, a body tension. Just notice it."
              accent="#3090A8"
            />
            <StepTile
              num="2"
              icon={Waves}
              title="Follow the tone"
              body="A gentle tone bounces left and right between your ears to help you time the bilateral movement. Let your eyes or hands follow along. If moving your eyes is too distracting, simply shift to tapping your thighs; this is often easier and just as effective."
              accent="#78C0D8"
            />
            <StepTile
              num="3"
              icon={Moon}
              title="Your brain settles"
              body="Like it does during deep REM sleep, your mind begins to soothe itself and loosen the grip of the feeling."
              accent="#E2B887"
            />
          </div>
        </section>

        {/* ============ THE SCIENCE ============ */}
        <section className="mb-10 animate-fade-in-up stagger-1">
          <div className="bg-white rounded-2xl p-6 sm:p-8 border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)]">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#EAF0F9] flex items-center justify-center flex-shrink-0">
                <Brain className="w-6 h-6 text-[#78C0D8]" />
              </div>
              <div>
                <h3 className="font-outfit font-medium text-lg text-[#1B2349] mb-2">A little science, in plain words</h3>
                <p className="text-[#1B2349] leading-relaxed mb-3">
                  Bilateral stimulation sounds fancy, but it's really quite simple. The idea is to gently get your brain switching back and forth between its left and right sides while you hold a problem or stressful feeling in mind. When that happens, something remarkable occurs — your brain kicks into a natural calming process, very similar to what it does on its own during deep REM sleep.
                </p>
                <p className="text-[#1B2349] leading-relaxed">
                  Science has shown us time and again that pairing this left-right brain activity with a stressful thought or feeling takes the edge off — easing the tension in your body, softening the emotion, and causing the problem to lose its grip.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ============ TWO METHODS ============ */}
        <section className="mb-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-6 bg-[#78C0D8] rounded-full" />
            <h2 className="font-outfit font-medium text-xl text-[#1B2349]">Two simple ways to follow along</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Method 1 */}
            <div className="relative bg-white rounded-2xl p-6 sm:p-7 border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_6px_28px_rgba(0,0,0,0.06)] transition-shadow animate-fade-in-up">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#78C0D8] to-[#AFC7F0] rounded-t-2xl" />
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-[#EAF0F9] rounded-xl flex items-center justify-center">
                  <Eye className="w-6 h-6 text-[#78C0D8]" />
                </div>
                <div>
                  <p className="text-xs font-medium text-[#78C0D8] uppercase tracking-wider">Method 1</p>
                  <h3 className="font-outfit font-medium text-lg text-[#1B2349]">Eye Movement</h3>
                </div>
              </div>
              <p className="text-[#1B2349] leading-relaxed mb-4">
                Picture yourself sitting on the center line of a tennis court, head perfectly still, watching the ball bounce from one side to the other. Let your eyes sweep smoothly from left to right and back again — wide enough that your gaze travels from one side of the room to the other, but without moving your head.
              </p>
              <div className="flex items-center gap-2 text-sm text-[#78C0D8] font-medium">
                <CheckCircle2 className="w-4 h-4" />
                Nice and easy
              </div>
            </div>

            {/* Method 2 */}
            <div className="relative bg-white rounded-2xl p-6 sm:p-7 border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_6px_28px_rgba(0,0,0,0.06)] transition-shadow animate-fade-in-up stagger-1">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#E2B887] to-[#F5D4A8] rounded-t-2xl" />
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-[#FEF3E7] rounded-xl flex items-center justify-center">
                  <Hand className="w-6 h-6 text-[#E2B887]" />
                </div>
                <div>
                  <p className="text-xs font-medium text-[#B8965A] uppercase tracking-wider">Method 2</p>
                  <h3 className="font-outfit font-medium text-lg text-[#1B2349]">Hand Tapping</h3>
                </div>
              </div>
              <p className="text-[#1B2349] leading-relaxed mb-4">
                Rest your palms on your thighs with your legs uncrossed, then gently tap left and right in time with the tone — keeping rhythm. You are welcome to close your eyes during the tapping.
              </p>
              <div className="flex items-center gap-2 text-sm text-[#B8965A] font-medium">
                <CheckCircle2 className="w-4 h-4" />
                Eyes open or closed — your choice
              </div>
            </div>
          </div>
        </section>

        {/* ============ CTA ============ */}
        <section className="relative overflow-hidden rounded-2xl bg-white p-8 sm:p-10 border border-slate-100 text-center shadow-[0_4px_20px_rgba(0,0,0,0.03)]">
          <div className="absolute inset-0 bg-gradient-to-br from-[#F4F7F6] via-white to-[#EAF0F9]/40 pointer-events-none" />
          <div className="relative">
            <h3 className="font-outfit font-semibold text-2xl text-[#1B2349] mb-3">Ready when you are</h3>
            <p className="text-[#718096] mb-6 max-w-md mx-auto">
              Pick a practice on the dashboard and try it. You can always come back here for a refresher.
            </p>
            <Button
              onClick={() => navigate('/dashboard')}
              className="btn-primary text-base px-8 py-5 rounded-full group"
              data-testid="start-journey-btn"
            >
              Start Your Journey
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </section>
      </main>

      {/* Mobile Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-100 px-6 py-3">
        <div className="flex items-center justify-around">
          <Link to="/dashboard" className="flex flex-col items-center gap-1 text-[#718096]">
            <Sparkles className="w-5 h-5" />
            <span className="text-xs">Home</span>
          </Link>
          <Link to="/getting-started" className="flex flex-col items-center gap-1 text-[#3090A8]">
            <BookOpen className="w-5 h-5" />
            <span className="text-xs">Guide</span>
          </Link>
          <Link to="/history" className="flex flex-col items-center gap-1 text-[#718096]">
            <History className="w-5 h-5" />
            <span className="text-xs">History</span>
          </Link>
          <Link to="/favorites" className="flex flex-col items-center gap-1 text-[#718096]">
            <Heart className="w-5 h-5" />
            <span className="text-xs">Favorites</span>
          </Link>
          <Link to="/settings" className="flex flex-col items-center gap-1 text-[#718096]">
            <Settings className="w-5 h-5" />
            <span className="text-xs">Settings</span>
          </Link>
        </div>
      </nav>
      <SiteFooter />
    </div>
  );
};

export default GettingStartedPage;
